import React, {forwardRef, useImperativeHandle, useEffect, useLayoutEffect, useCallback, useRef, memo} from 'react'
import {useSelector} from 'react-redux'
import {getAllUserDetails} from '../../../accounts/Overview/OverviewSlice/UserDetailsSlice'
import {ENV} from '../../../../../Utils/_helpers/EnvHelper'
import {useContentEditable} from '../../../../../Components/hooks/useContentEditable'
import {DOMUtils} from '../../../../../Utils/domUtils'

export interface ContentEditableInputV3Ref {
  insertPlaceholder: (placeholder: string) => void
  insertEmoji: (emoji: string) => void
  insertText: (text: string) => void
  insertUrl: (url: string) => void
  focus: () => void
  getRawText: () => string
  clear: () => void
}

export interface ContentEditableInputV3Props {
  value: string
  onChange: (value: string) => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void
  disabled?: boolean
  placeholder?: string
  maxLength?: number
  className?: string
}

export const ContentEditableInputV3 = memo(
  forwardRef<ContentEditableInputV3Ref, ContentEditableInputV3Props>(
  (
    {
      value,
      onChange,
      onKeyDown,
      disabled = false,
      placeholder = 'Type a message...',
      maxLength = 1600,
      className,
    },
    ref
  ) => {
    const userValues: any = useSelector(getAllUserDetails)
    const defaultDomain = `${ENV().REACT_APP_SHORTURL}`
    const isFirstRender = useRef(true)
    const lastReportedValueRef = useRef(value)

    const savedRangeRef = useRef<Range | null>(null)
    const pendingEmojiNodeRef = useRef<Node | null>(null)

    // Save cursor position when contenteditable loses focus
    const handleBlur = useCallback(() => {
      const selection = window.getSelection()
      if (selection && selection.rangeCount > 0) {
        savedRangeRef.current = selection.getRangeAt(0).cloneRange()
      }
    }, [])

    // Build domain options for URL badge detection
    const domainOptions: string[] = userValues?.customShortLink?.status
      ? [defaultDomain, userValues.customShortLink?.domain].filter(Boolean)
      : [defaultDomain]

    // Wrap onChange to track internally-reported values
    const trackedOnChange = useCallback(
      (val: string) => {
        lastReportedValueRef.current = val
        onChange(val)
      },
      [onChange]
    )

    // Use the existing content editable hook
    const {
      handleInput,
      handleKeyDown: hookHandleKeyDown,
      insertPlaceholder,
      contentEditableRef,
      updateContentEditable,
      getRawText,
      handlePaste,
    } = useContentEditable({
      maxLength,
      onChange: trackedOnChange,
      initialValue: value,
      domainOptions,
      isEmail: false,
    })

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      insertPlaceholder,
      insertEmoji: (emoji: string) => {
        const contentEditable = contentEditableRef.current
        if (!contentEditable) return

        const selection = window.getSelection()
        if (!selection) return

        let range: Range

        if (savedRangeRef.current && contentEditable.contains(savedRangeRef.current.startContainer)) {
          range = savedRangeRef.current
          selection.removeAllRanges()
          selection.addRange(range)
          range = selection.getRangeAt(0)
        } else if (selection.rangeCount > 0 && contentEditable.contains(selection.anchorNode)) {
          range = selection.getRangeAt(0)
        } else {
          range = document.createRange()
          range.setStart(contentEditable, contentEditable.childNodes.length)
          range.collapse(true)
          selection.removeAllRanges()
          selection.addRange(range)
        }

        const emojiNode = document.createTextNode(emoji)
        range.insertNode(emojiNode)

        if (!emojiNode.isConnected) {
          // insertNode failed silently - bail out and fall through to
          // handleInput which will pick up the stale state
          handleInput()
          return
        }

        const afterRange = document.createRange()
        afterRange.setStartAfter(emojiNode)
        afterRange.collapse(true)
        selection.removeAllRanges()
        selection.addRange(afterRange)

        // Mark pending BEFORE handleInput so useLayoutEffect can restore
        // cursor during the React re-render (handleInput triggers onChange)
        pendingEmojiNodeRef.current = emojiNode

        // Trigger onChange via handleInput (keeps the hook's internal
        // lastValueRef in sync so updateContentEditable is skipped)
        handleInput()

        // Deferred focus: calling contentEditable.focus() resets the
        // cursor to position 0. Set the range before focusing so the
        // cursor lands at the emoji position, not the beginning.
        // Double RAF ensures both React effects and browser layout settle.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const sel = window.getSelection()
            if (!sel) return

            if (emojiNode.isConnected) {
              const r = document.createRange()
              r.setStartAfter(emojiNode)
              r.collapse(true)
              sel.removeAllRanges()
              sel.addRange(r)
            } else {
              // Emoji node was removed (e.g. updateContentEditable reset
              // innerHTML). Place cursor at the end as fallback.
              const r = document.createRange()
              r.setStart(contentEditable, contentEditable.childNodes.length)
              r.collapse(true)
              sel.removeAllRanges()
              sel.addRange(r)
            }
            contentEditable.focus()
          })
        })
      },
      insertText: (text: string) => {
        const contentEditable = contentEditableRef.current
        if (!contentEditable) return

        contentEditable.focus()

        const selection = window.getSelection()
        if (!selection) return

        let range: Range
        if (selection.rangeCount === 0) {
          range = document.createRange()
          range.setStart(contentEditable, contentEditable.childNodes.length)
        } else {
          range = selection.getRangeAt(0)
        }

        // Insert text node with space after
        const textNode = document.createTextNode(text + ' ')
        range.insertNode(textNode)

        // Move cursor after text
        const afterRange = document.createRange()
        afterRange.setStartAfter(textNode)
        afterRange.collapse(true)
        selection.removeAllRanges()
        selection.addRange(afterRange)

        // Trigger onChange
        handleInput()
      },
      insertUrl: (url: string) => {
        const contentEditable = contentEditableRef.current
        if (!contentEditable) return

        contentEditable.focus()

        const selection = window.getSelection()
        if (!selection) return

        let range: Range
        if (selection.rangeCount === 0) {
          range = document.createRange()
          range.setStart(contentEditable, contentEditable.childNodes.length)
        } else {
          range = selection.getRangeAt(0)
        }

        // Create URL badge element
        const badge = DOMUtils.createBadgeElement({url})
        const space = document.createTextNode('\u00A0')

        // Create fragment with badge and space
        const frag = document.createDocumentFragment()
        frag.appendChild(badge)
        frag.appendChild(space)

        // Insert at cursor position
        range.insertNode(frag)

        // Move cursor after space
        const afterRange = document.createRange()
        afterRange.setStartAfter(space)
        afterRange.collapse(true)
        selection.removeAllRanges()
        selection.addRange(afterRange)

        // Trigger onChange
        handleInput()
      },
      focus: () => {
        contentEditableRef.current?.focus()
      },
      getRawText,
      clear: () => {
        if (contentEditableRef.current) {
          contentEditableRef.current.innerHTML = ''
          onChange('')
        }
      },
    }))

    // Sync external value changes to content editable
    useEffect(() => {
      // Skip first render - hook handles initial value
      if (isFirstRender.current) {
        isFirstRender.current = false
        return
      }

      // Only update if value changed externally (e.g., template selection)
      // Use lastReportedValueRef to avoid re-syncing when we were the ones
      // who called onChange (e.g. after direct DOM mutation like emoji insert)
      if (value !== lastReportedValueRef.current) {
        updateContentEditable(value)
      }
    }, [value, updateContentEditable])

    // Register badge delete handler
    useEffect(() => {
      return DOMUtils.registerBadgeDeleteHandler(contentEditableRef.current, handleInput)
    }, [contentEditableRef, handleInput])

    // Restore cursor after React reconciliation if a pending emoji was inserted.
    // Runs synchronously after DOM mutations, before the browser paints.
    useLayoutEffect(() => {
      if (pendingEmojiNodeRef.current) {
        const node = pendingEmojiNodeRef.current
        if (node.isConnected) {
          const sel = window.getSelection()
          if (sel) {
            const range = document.createRange()
            range.setStartAfter(node)
            range.collapse(true)
            sel.removeAllRanges()
            sel.addRange(range)
          }
        }
        pendingEmojiNodeRef.current = null
      }
    })

    // Handle key down - combine hook handler with custom handler
    const handleKeyDownCombined = useCallback(
      (e: React.KeyboardEvent<HTMLDivElement>) => {
        hookHandleKeyDown(e)
        onKeyDown?.(e)
      },
      [hookHandleKeyDown, onKeyDown]
    )

    return (
      <div
        ref={contentEditableRef}
        contentEditable={!disabled}
        onInput={handleInput}
        onKeyDown={handleKeyDownCombined}
        onPaste={handlePaste}
        onBlur={handleBlur}
        data-placeholder={disabled ? 'Select a conversation' : placeholder}
        className={`contenteditable-input-v3 ${className || ''}`}
        style={{
          minHeight: '60px',
          maxHeight: '150px',
          overflowY: 'auto',
          outline: 'none',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      />
    )
  }
))

ContentEditableInputV3.displayName = 'ContentEditableInputV3'

export default ContentEditableInputV3